import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Building, 
  MessageSquare, 
  DollarSign, 
  TrendingUp,
  Phone,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    totalConversations: 0,
    totalRevenue: 0,
    availableNumbers: 0,
    usedNumbers: 0
  });
  const [loading, setLoading] = useState(true);

  // Dados simulados para gráficos
  const monthlyData = [
    { month: 'Jan', clients: 12, revenue: 2400 },
    { month: 'Fev', clients: 19, revenue: 3800 },
    { month: 'Mar', clients: 25, revenue: 5000 },
    { month: 'Abr', clients: 32, revenue: 6400 },
    { month: 'Mai', clients: 38, revenue: 7600 },
    { month: 'Jun', clients: 45, revenue: 9000 },
  ];

  const planDistribution = [
    { name: 'Básico', value: 60, color: '#3b82f6' },
    { name: 'Pro', value: 30, color: '#f97316' },
    { name: 'Enterprise', value: 10, color: '#10b981' }
  ];

  const recentClients = [
    {
      id: 1,
      businessName: 'Tech Store',
      email: 'contato@techstore.com',
      plan: 'Pro',
      status: 'active',
      joinedAt: '2024-09-15',
      phoneNumber: '+55 11 99999-0001'
    },
    {
      id: 2,
      businessName: 'Fashion Boutique',
      email: 'vendas@fashionboutique.com',
      plan: 'Básico',
      status: 'active',
      joinedAt: '2024-09-14',
      phoneNumber: '+55 11 99999-0002'
    },
    {
      id: 3,
      businessName: 'Consultoria XYZ',
      email: 'info@consultoriaxyz.com',
      plan: 'Enterprise',
      status: 'pending',
      joinedAt: '2024-09-13',
      phoneNumber: 'Pendente'
    }
  ];

  useEffect(() => {
    // Simular carregamento de dados
    setTimeout(() => {
      setStats({
        totalClients: 127,
        activeClients: 118,
        totalConversations: 3456,
        totalRevenue: 45600.00,
        availableNumbers: 23,
        usedNumbers: 118
      });
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-secondary-100 text-secondary-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'pending':
        return 'Pendente';
      case 'inactive':
        return 'Inativo';
      default:
        return 'Desconhecido';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 font-display">
            Painel Administrativo
          </h1>
          <p className="text-secondary-600 mt-1">
            Visão geral da plataforma BIZREP
          </p>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Building className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total de Clientes</p>
                <p className="text-2xl font-bold text-secondary-900">{stats.totalClients}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Clientes Ativos</p>
                <p className="text-2xl font-bold text-secondary-900">{stats.activeClients}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-purple-100 p-3 rounded-lg">
                <MessageSquare className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Conversas Totais</p>
                <p className="text-2xl font-bold text-secondary-900">{stats.totalConversations.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="bg-primary-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Receita Total</p>
                <p className="text-2xl font-bold text-secondary-900">R$ {stats.totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Gestão de Números WhatsApp */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2" />
              Gestão de Números
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-secondary-600">Números Disponíveis</span>
                <span className="text-2xl font-bold text-green-600">{stats.availableNumbers}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-secondary-600">Números em Uso</span>
                <span className="text-2xl font-bold text-blue-600">{stats.usedNumbers}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-secondary-600">Total de Números</span>
                <span className="text-2xl font-bold text-secondary-900">{stats.availableNumbers + stats.usedNumbers}</span>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex justify-between text-sm text-secondary-600 mb-2">
                <span>Utilização</span>
                <span>{((stats.usedNumbers / (stats.availableNumbers + stats.usedNumbers)) * 100).toFixed(1)}%</span>
              </div>
              <div className="w-full bg-secondary-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(stats.usedNumbers / (stats.availableNumbers + stats.usedNumbers)) * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {stats.availableNumbers < 10 && (
                <div className="flex items-center text-yellow-600 text-sm">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Poucos números disponíveis
                </div>
              )}
              <button className="w-full btn-primary text-sm">
                Adquirir Novos Números
              </button>
            </div>
          </div>

          {/* Gráfico de Crescimento */}
          <div className="lg:col-span-2 card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              Crescimento Mensal
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Bar yAxisId="left" dataKey="clients" fill="#3b82f6" name="Novos Clientes" />
                <Bar yAxisId="right" dataKey="revenue" fill="#f97316" name="Receita (R$)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Distribuição de Planos e Clientes Recentes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Distribuição de Planos */}
          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              Distribuição de Planos
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={planDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {planDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center space-x-4 mt-4">
              {planDistribution.map((plan, index) => (
                <div key={index} className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: plan.color }}
                  ></div>
                  <span className="text-sm text-secondary-600">{plan.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Clientes Recentes */}
          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              Clientes Recentes
            </h3>
            <div className="space-y-4">
              {recentClients.map((client) => (
                <div key={client.id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-secondary-900">{client.businessName}</h4>
                    <p className="text-sm text-secondary-600">{client.email}</p>
                    <p className="text-xs text-secondary-500">
                      Cadastrado em {new Date(client.joinedAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(client.status)}`}>
                      {getStatusText(client.status)}
                    </span>
                    <p className="text-xs text-secondary-500 mt-1">{client.plan}</p>
                    <p className="text-xs text-secondary-500">{client.phoneNumber}</p>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 text-primary-600 hover:text-primary-500 text-sm font-medium">
              Ver Todos os Clientes
            </button>
          </div>
        </div>

        {/* Alertas do Sistema */}
        <div className="mt-8 card">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">
            Status do Sistema
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-green-900">APIs Funcionando</p>
                <p className="text-sm text-green-600">Todas as integrações operacionais</p>
              </div>
            </div>
            
            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-green-900">Banco de Dados</p>
                <p className="text-sm text-green-600">Conectado e responsivo</p>
              </div>
            </div>
            
            <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-3" />
              <div>
                <p className="font-medium text-yellow-900">Números WhatsApp</p>
                <p className="text-sm text-yellow-600">Estoque baixo - considere adquirir mais</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;

